import './brokerIAInvestmentModule.scss';

import React, { useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { i18n } from 'core/i18n';

import { LoadingSection } from 'commonsComponents/loadingSection';
import { recordLinkV2, recordPage } from 'utilities/tagging';
import { IA_INVESTMENT_HOME_INTERACTIONS } from 'constants/tagging';
import BrokerMyInvestmentsActions from 'actions/brokerMyInvestments';
import PredictionSearchActions from 'actions/brokerIA/brokerIAGetPredictionSearch';

import BrokerIAPagination from '../brokerIAPagination/brokerIAPagination';
import Disclaimers from '../shared/disclaimers/disclaimers';
import Filters from '../shared/filters/filters';
import StockCard from '../shared/stockCard/stockCard';
import { useFilters } from '../../hooks/useFilters';

const noResultsIcon = require('assets/icons/icon-brokerIA-no-items.svg');
const errorIcon = require('assets/icons/icon-brokerIA-error.svg');

const { formatText } = i18n;

const BrokerIAInvestment = ({
    compact = false,
    country = 'ES',
    immMyInvestments,
    fetchMyInvestments,
    getPredictionSearchResults,
    predictionSearchResults,
    shouldRequestPGInfo = true,
}) => {
    const { filtersData, filtersParams } = useFilters(compact);

    const products = predictionSearchResults.get('results').toJS();
    const totalPages = predictionSearchResults.get('totalPages');

    useEffect(() => {
        if (
            !immMyInvestments.get('fetchSuccess') &&
            !immMyInvestments.get('isFetching') &&
            !immMyInvestments.get('errorReceived') &&
            shouldRequestPGInfo
        ) {
            fetchMyInvestments();
        }
    }, []);

    useEffect(() => {
        getPredictionSearchResults(filtersParams);
    }, [
        filtersParams.horizon,
        filtersParams.sorting,
        filtersParams.itemsPage,
        filtersParams.page,
        filtersParams.micCode,
        filtersParams.asset,
    ]);

    useEffect(() => {
        recordStateChanges();
    }, [predictionSearchResults]);

    const getSellLink = (isin, micCode) => {
        if (
            !immMyInvestments ||
            immMyInvestments.get('errorReceived') ||
            !immMyInvestments.getIn(['products', 'stocks', 'stocks'])
        ) {
            return '';
        }

        const stockInWallet = immMyInvestments
            .getIn(['products', 'stocks', 'stocks'])
            .find(stock => stock.get('isin') === isin && stock.get('iso') === micCode);

        if (stockInWallet && stockInWallet.get('shares')) {
            const emisionCode = stockInWallet.get('emisionCode');
            const valueCode = stockInWallet.get('valueCode');
            const sellLink = `/myprofile/broker/stocksSell/${isin}/${micCode}/${emisionCode}/${valueCode}`;

            return emisionCode && valueCode ? sellLink : '';
        }
        return '';
    };

    const doRecordViewMoreActionsLinkClick = () => {
        recordLinkV2({
            categoriaEvento: IA_INVESTMENT_HOME_INTERACTIONS,
            accionEvento: 'clickLink',
            etiquetaEvento: 'seeMoreStocks',
        });
    };

    const renderStockCard = () => {
        if (predictionSearchResults.get('isFetching')) {
            return (
                <div className="brokerIA-investment__loading">
                    <LoadingSection />
                </div>
            );
        }

        if (predictionSearchResults.get('isFetched') && predictionSearchResults.get('results')) {
            return products.length ? (
                <div>
                    {products.map((stockData, i) => (
                        <StockCard
                            compact={compact}
                            stockData={stockData}
                            index={i}
                            key={i}
                            sellLink={getSellLink(
                                stockData.isin,
                                stockData.marketLocationDto.micCode
                            )}
                        />
                    ))}

                    {!compact && (
                        <BrokerIAPagination
                            currentPage={filtersData.page}
                            onPagePress={filtersData.handlePage}
                            totalPages={totalPages} 
                        />
                    )}
                </div>
            ) : (
                <div className="brokerIA-investment__no-results">
                    <img src={noResultsIcon} alt="Sin resultados" />
                    <p>
                        {formatText('brokerIA-investment_no_results_text')}{' '}
                        <a href="/myprofile/broker/explore">
                            {formatText('brokerIA-investment_no_results_link')}
                        </a>
                        .
                    </p>
                </div>
            );
        }

        return (
            <div className="brokerIA-investment__error">
                <img className="brokerIA-investment__error_icon" src={errorIcon} alt="error" />

                <p className="brokerIA-investment__error_description">
                    {formatText('brokerIA-error_description')}
                </p>

                <button
                    onClick={() => getPredictionSearchResults(filtersParams)}
                    className="brokerIA-investment__error_button"
                >
                    {formatText('brokerIA-error_button')}
                </button>
            </div>
        );
    };

    const className = classNames('brokerIA-investment', {
        'brokerIA-investment--compact': !!compact,
    });

    const wrapperClassName = classNames('brokerIA-investment__wrapper', {
        'brokerIA-investment--compact__wrapper': !!compact,
    });

    const recordLoadSuccess = () => {
        const {
            nombrePagina,
            tipoPagina,
            zonaWeb,
            tipoUsusario,
            estadoUsuario,
            identificadorUsuario,
        } = window.utag_data;

        const customTagging = {
            nombrePagina,
            tipoPagina,
            zonaWeb,
            tipoUsusario,
            estadoUsuario,
            identificadorUsuario,

            // Enhanced Ecommerce Payload
            tealium_event: 'product_impression',
            product_impression_name: products.map(({ description }) => description),
            product_impression_id: products.map(({ isin }) => isin),
            product_impression_list: ['iainvestment', 'iainvestment', 'iainvestment'],
            product_impression_position: products.map(
                (_, index) => index + 1 + filtersParams.itemsPage * filtersParams.page
            ),
        };

        const tagNoResults = {
            categoriaEvento: "IAInvestmentHomeInteractions",
            accionEvento: "search",
            etiquetaEvento: "noResults"
            }

        if (products.length) {
            recordPage(customTagging);
        } else {
            recordLinkV2(tagNoResults);
        }
    };

    const recordLoadError = () => {
        const {
            nombrePagina,
            tipoPagina,
            zonaWeb,
            tipoUsusario,
            estadoUsuario,
            identificadorUsuario,
        } = window.utag_data;

        const customTagging = {
            nombrePagina,
            tipoPagina,
            zonaWeb,
            tipoUsusario,
            estadoUsuario,
            identificadorUsuario,

            categoriaError: 'error de conexion',
            descripcionError: 'error de conexión a internet, inténtalo de nuevo en unos minutos',
        };

        recordPage(customTagging);
    };

    const recordStateChanges = () => {
        if (predictionSearchResults.get('error')) {
            recordLoadError();
        } else if (predictionSearchResults.get('isFetched')) {
            recordLoadSuccess();
        }
    };

    return (
        <div className={className}>
            <div className={wrapperClassName}>
                {compact && (
                    <div className="brokerIA-investment__header">
                        <h3>{formatText('brokerIA-investment_title')}</h3>
                        <p>{formatText('brokerIA-investment_subtitle')}</p>
                    </div>
                )}
                <div className="brokerIA-investment__content">
                    <Filters compact={compact} country={country} filtersData={filtersData} />

                    {renderStockCard()}

                    {compact && (
                        <Link
                            className="brokerIA-investment__link"
                            onClick={doRecordViewMoreActionsLinkClick}
                            to={'/myprofile/broker/broker-artificial-intelligence'}
                        >
                            <strong>{formatText('brokerIA-investment_link')}</strong>
                            <i className="brokerIA-investment__link-arrow-icon" />
                        </Link>
                    )}
                </div>
                <div className="brokerIA-investment__footer">
                    <Disclaimers compact={compact} />
                </div>
            </div>
        </div>
    );
};

BrokerIAInvestment.propTypes = {
    compact: PropTypes.Boolean,
    country: PropTypes.String,
    immMyInvestments: PropTypes.object.isRequired,
    fetchMyInvestments: PropTypes.func,
    getPredictionSearchResults: PropTypes.func,
    predictionSearchResults: PropTypes.object,
    shouldRequestPGInfo: PropTypes.Boolean,
};

export default connect(
    state => ({
        immMyInvestments: state.brokerMyInvestments,
        predictionSearchResults: state.brokerIAPredictionSearch,
    }),
    {
        fetchMyInvestments: BrokerMyInvestmentsActions.fetchMyInvestments,
        getPredictionSearchResults: PredictionSearchActions.getPredictionSearchResults,
    }
)(withRouter(BrokerIAInvestment));
